import { useTranslation } from 'next-i18next/pages';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import getRegExp from '@/utils/getRegExp';

import { Label, LabelForm } from '@/components/shared/ui/forms/common';
import InputBoxWithHookForm from '@/components/shared/ui/forms/inputs/InputBoxWithHookForm';
import { SelectBoxWithHookForm } from '@/components/shared/ui/forms/selects';
import TextareaBoxWithHookForm from '@/components/shared/ui/forms/textareas/TextareaBoxWithHookForm';

import {
  getInquiryInputA11y,
  getInquiryLabelA11y,
  getInquirySelectA11y,
} from './InquiryFormInputs.a11y';
import {
  inputItemListClass,
  inputItemRowClass,
  inputItemRowTitleClass,
  inquiryInputsContainerClass,
} from './InquiryFormInputs.styles';
import InquirySourceFields from './InquirySourceFields';
import useInquiryFormOptions from './useInquiryFormOptions';

export default function InquiryInputs() {
  const {
    register,
    formState: { errors },
    watch,
    control,
  } = useFormContext();

  const { t } = useTranslation('common');
  const { employeesOptions, interestOptions } = useInquiryFormOptions();
  const label = getInquiryLabelA11y;
  const input = getInquiryInputA11y;
  const select = getInquirySelectA11y;

  return (
    <div className={inquiryInputsContainerClass}>
      <div className={inputItemListClass}>
        <div className={inputItemRowClass}>
          <h4 className={inputItemRowTitleClass}>{t('기본 정보')}</h4>
          <div>
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('writer')} text={t('이름')} required />
              }
              formComponent={
                <InputBoxWithHookForm
                  {...register('writer', {
                    required: t('이름을 입력해 주세요.'),
                  })}
                  {...input('writer')}
                  autoComplete="name"
                  placeholder={t('이름')}
                  errors={errors}
                />
              }
            />
          </div>
          <div>
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('email')} text={t('회신 이메일')} required />
              }
              formComponent={
                <InputBoxWithHookForm
                  {...register('email', {
                    required: t('이메일을 입력해 주세요.'),
                    pattern: {
                      value: getRegExp('email'),
                      message: t('올바른 형식으로 입력해 주세요.'),
                    },
                  })}
                  {...input('email')}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  spellCheck={false}
                  placeholder={t('이메일')}
                  errors={errors}
                />
              }
            />
          </div>
          <div>
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('phone')} text={t('전화번호 (선택)')} />
              }
              formComponent={
                <InputBoxWithHookForm
                  {...register('phone')}
                  {...input('phone')}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+82 10-1234-5678"
                  description={t('전화 상담을 원하시면 남겨주세요')}
                  errors={errors}
                />
              }
            />
          </div>
          <InquirySourceFields />
        </div>

        <div className={inputItemRowClass}>
          <h4 className={inputItemRowTitleClass}>{t('회사 정보')}</h4>
          <div>
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('title')} text={t('회사명')} required />
              }
              formComponent={
                <InputBoxWithHookForm
                  {...register('title', {
                    required: t('회사명을 입력해 주세요.'),
                  })}
                  {...input('title')}
                  autoComplete="organization"
                  placeholder={t('회사명')}
                  errors={errors}
                />
              }
            />
          </div>
          <div>
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('employees')} text={t('회사 규모')} required />
              }
              formComponent={
                <SelectBoxWithHookForm
                  name="employees"
                  {...select('employees')}
                  rules={{ required: t('필수 입력 사항입니다.') }}
                  control={control}
                  errors={errors}
                  options={employeesOptions}
                  placeholder={t('임직원 수')}
                  isClearable={false}
                />
              }
            />
          </div>
          <div>
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('subtitle')} text={t('부서')} required />
              }
              formComponent={
                <InputBoxWithHookForm
                  {...register('subtitle', {
                    required: t('부서명을 입력해 주세요.'),
                  })}
                  {...input('subtitle')}
                  autoComplete="off"
                  placeholder={t('부서명')}
                  errors={errors}
                />
              }
            />
          </div>
          <div>
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('position')} text={t('직위/직책')} required />
              }
              formComponent={
                <InputBoxWithHookForm
                  {...register('position', {
                    required: t('직위/직책을 입력해 주세요.'),
                  })}
                  {...input('position')}
                  autoComplete="organization-title"
                  placeholder={t('직위/직책')}
                  errors={errors}
                />
              }
            />
          </div>
        </div>

        <div className={inputItemRowClass}>
          <h4 className={inputItemRowTitleClass}>{t('문의 내용')}</h4>
          <div className="lg:col-span-2">
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('interest')} text={t('관심 분야')} required />
              }
              formComponent={
                <SelectBoxWithHookForm
                  name="interest"
                  {...select('interest')}
                  rules={{ required: t('필수 입력 사항입니다.') }}
                  control={control}
                  errors={errors}
                  options={interestOptions}
                  placeholder={t('문의 분야')}
                  isClearable={false}
                />
              }
            />
          </div>
          <div className="lg:col-span-2">
            <LabelForm
              align="vertical"
              labelComponent={
                <Label {...label('content')} text={t('문의 내용')} required />
              }
              formComponent={
                <TextareaBoxWithHookForm
                  {...register('content', {
                    required: t('문의 내용을 입력해 주세요.'),
                    maxLength: {
                      value: 500,
                      message: t('최대 500자까지 작성 가능합니다.'),
                    },
                  })}
                  {...input('content')}
                  autoComplete="off"
                  watch={watch}
                  maxLength={500}
                  placeholder={t('문의 내용을 작성해주세요')}
                  errors={errors}
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
